import React from 'react';
import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../../../../infrastructure/sub-windows';
import { handleApiException, showNotification } from '../../../../../global/modal/alerts-host/alert-templates';
import {
    EmailTemplateType,
    IAvailableTypes,
    IEmailConfigurationDAO,
    IEmailsTemplateConfiguration,
    IEmailTemplateFields,
    IEmailTemplatesErrorConfig
} from '../../../../../../api/email-configuration.dao';

import { calcByteCodeSize } from '../../../../../../infrastructure/utils/common.utils';
import { LONG_RICH_TEXT_LENGTH, SHORT_PLAIN_TEXT_LENGTH } from '../../../../../../constants/common.constants';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { EmailsTemplateForm } from './emails-template-form';
import UploadImageS3VM from '../../../shared/upload-image/upload-image-to-s3.viewmodel';
import { CreateEmailTemplatesForm } from './create-email-template-form/create-email-template-form';
import { IAlertsService } from '../../../../../../infrastructure/alerts';
import Text from '../../../../../global/text/text';
import { Space } from '../../../../../../constants/html-character-entities';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../../../../global/rich-text-editor/utils/transpiler';
import { REGISTRATION_CALENDAR_INVITATION } from './emails-template-form.texts';


class EmailsTemplateFormVM {
    @mobx.observable
    public isEmailFormChanged = false;
    @mobx.observable
    public serverErrorMessage = '';
    @mobx.observable
    public saving: boolean;
    @mobx.observable
    public loading: boolean;
    @mobx.observable
    public emailTemplates: IEmailTemplateFields[];
    @mobx.observable
    public initialEmailTemplates: IEmailTemplateFields[];
    @mobx.observable
    public emailTemplatesNames: string[] = [];
    @mobx.observable
    public selectedEmailTemplate: IEmailTemplateFields;
    @mobx.observable
    public emailTemplatesErrorHandler: Record<string, IEmailTemplatesErrorConfig> = {};
    @mobx.observable
    public emailBodyErrors: string[] = [];
    @mobx.observable
    public uploadEmailLogoImageS3VM: UploadImageS3VM = null;

    private readonly accountId: number;
    private readonly eventId: number;

    private toTrackInfo: { [key: string]: string };

    @mobx.observable
    public currentTemplate = '';
    @mobx.observable
    private showConfigureEmailTemplateFormPopupMap: ISubWindowFacade;
    @mobx.observable
    private createEmailTemplateFormPopupMap: ISubWindowFacade;

    @mobx.observable
    public triagedRegistrationEnabled = false;
    @mobx.observable
    public availableSubtypes: string[] = [];
    @mobx.observable
    public availableTypes: IAvailableTypes[] = [];

    constructor(
        public props: {
            accountId: number;
            eventId: number;
            intl: IntlShape;
        },
        private readonly emailConfigurationDAO: IEmailConfigurationDAO = IoC.Container.get(IEmailConfigurationDAO),
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
    ) {
        this.accountId = props.accountId;
        this.eventId = props.eventId;
        this.uploadEmailLogoImageS3VM = new UploadImageS3VM(
            this.accountId,
            this.eventId,
            'logo',
            'email-configuration',
            null,
            null,
            true
        );
    }

    private setInitialToTrackInfo(templates: IEmailTemplateFields[]) {
        this.toTrackInfo = templates.reduce((acc, template) => {
            acc[template.name] = 'No';
            return acc;
        }, {});
    }

    @mobx.action
    public setEmailTemplatesNames(templates: IEmailTemplateFields[]): void {
        this.emailTemplatesNames = templates.map(t => t.name);
    }

    @mobx.action
    public onUpdateEmailLogoError(error): string {
        return this.errorParsingService.extractMessage(error);
    }
    @mobx.action
    public emailFormChanged(value: boolean): void {
        this.isEmailFormChanged = value;
    }

    public get getSelectedTemplateSubject(): string {
        return this.selectedEmailTemplate.subject;
    }

    public get getSelectedTemplateBody(): string {
        return this.selectedEmailTemplate.body;
    }

    public get getSelectedTemplateLocation(): string {
        return this.selectedEmailTemplate.location;
    }

    public get getSelectedTemplateSubtype(): string {
        return this.selectedEmailTemplate.subtype;
    }

    public get isCustomEmailTemplate(): boolean {
        return !!this.selectedEmailTemplate.subtype;
    }

    public get getSelectedTemplateSubjectError(): string {
        return this.emailTemplatesErrorHandler[this.currentTemplate]?.emailSubjectError;
    }

    public get getSelectedTemplateLocationError(): string {
        return this.emailTemplatesErrorHandler[this.currentTemplate]?.emailLocationError;
    }

    @mobx.action
    public toggleEmailEnabled(): void {
        this.emailFormChanged(true);
        this.selectedEmailTemplate.enabled = !this.selectedEmailTemplate.enabled;
    }

    @mobx.action
    public toggleAutoSubscribedEnabled(): void {
        this.emailFormChanged(true);
        this.selectedEmailTemplate.autoSubscribed = !this.selectedEmailTemplate.autoSubscribed;
    }

    @mobx.action
    public setEmailSubject(value: string): void {
        this.selectedEmailTemplate.subject = value;
        this.toTrackInfo[this.currentTemplate] = 'Yes';
        if (!this.isEmailFormChanged) {
            this.emailFormChanged(true);
        }
        this.setEmailSubjectError(value);
    }

    @mobx.action
    public setEmailSubjectError(value: string): void {
        if (value.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            this.emailTemplatesErrorHandler[`${this.currentTemplate}`] = {
                ...this.emailTemplatesErrorHandler[`${this.currentTemplate}`],
                emailSubjectError: this.props.intl.formatMessage({
                    description: 'Warning message',
                    defaultMessage: 'Email subject is too long',
                    id: 'oe.common.emailSubjectTooLong',
                })
            };
        } else if (!value.trim().length) {
            this.emailTemplatesErrorHandler[`${this.currentTemplate}`] = {
                ...this.emailTemplatesErrorHandler[`${this.currentTemplate}`],
                emailSubjectError: this.props.intl.formatMessage({
                    description: 'Warning message',
                    defaultMessage: 'Email subject is required',
                    id: 'oe.common.emailSubjectRequired',
                })
            };
        } else {
            delete this.emailTemplatesErrorHandler[`${this.currentTemplate}`]?.emailSubjectError;
        }
    }

    @mobx.action
    public setEmailBody(value: string): void {
        this.selectedEmailTemplate.body = value;
        const error = this.getTemplateEmailBodyError(this.selectedEmailTemplate.name);
        if (error) {
            this.emailTemplatesErrorHandler[this.selectedEmailTemplate.name] = {
                ...this.emailTemplatesErrorHandler[this.selectedEmailTemplate.name],
                emailBodyError: error
            };
        } else {
            delete this.emailTemplatesErrorHandler[`${this.currentTemplate}`]?.emailBodyError;
        }
        this.toTrackInfo[this.currentTemplate] = 'Yes';
        if (!this.isEmailFormChanged) {
            this.emailFormChanged(true);
        }
    }

    private getTemplateEmailBodyError(template: string): string | null {
        const selectedEmailTemplate = this.emailTemplates.find(t => {
            return t.name === template;
        });
        if (selectedEmailTemplate.type === EmailTemplateType.REGISTRATION_CALENDAR_INVITATION) {
            return calcByteCodeSize(selectedEmailTemplate.body) > LONG_RICH_TEXT_LENGTH ?
                this.props.intl.formatMessage({
                    description: 'Warning message',
                    defaultMessage: 'Email body is too long',
                    id: 'oe.common.emailBodyTooLong',
                }) :
                null
            ;
        }
        return calcByteCodeSize(selectedEmailTemplate.body) > LONG_RICH_TEXT_LENGTH ?
            this.props.intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Email body is too long',
                id: 'oe.common.emailBodyTooLong',
            }) : !selectedEmailTemplate.body.length ?
            this.props.intl.formatMessage({
                description: 'Warning message',
                defaultMessage: 'Email body is required',
                id: 'oe.common.emailBodyRequired',
            }) :
            null
        ;
    }

    @mobx.action
    public setTemplateEmailLocation(value: string): void {
        this.selectedEmailTemplate.location = value;
        this.toTrackInfo[this.currentTemplate] = 'Yes';

        if (!this.isEmailFormChanged) {
            this.emailFormChanged(true);
        }
        this.setTemplateEmailLocationError(value);
    }

    @mobx.action
    private setTemplateEmailLocationError(value: string): void {
        if (value.trim().length > SHORT_PLAIN_TEXT_LENGTH) {
            this.emailTemplatesErrorHandler[`${this.currentTemplate}`] = {
                ...this.emailTemplatesErrorHandler[`${this.currentTemplate}`],
                emailLocationError: this.props.intl.formatMessage({
                    description: 'Warning message',
                    defaultMessage: 'Location is too long',
                    id: 'oe.common.emailSubjectTooLong',
                })
            };
        } else {
            delete this.emailTemplatesErrorHandler[`${this.currentTemplate}`]?.emailLocationError;
        }
    }

    public get getTemplateEmailErrors(): [string[], boolean] {
        const errorsInTemplates = this.emailTemplatesNames
            .reduce((acc, t) => {
                let isErrorExistInTemplate = !!this.emailTemplatesErrorHandler[t]?.emailBodyError
                    || !!this.emailTemplatesErrorHandler[t]?.emailSubjectError;
                if (t.startsWith(REGISTRATION_CALENDAR_INVITATION)) {
                    isErrorExistInTemplate = isErrorExistInTemplate || !!this.emailTemplatesErrorHandler[t]?.emailLocationError;
                }
                if (isErrorExistInTemplate) {
                    acc[t] = isErrorExistInTemplate;
                }
                return acc;
            }, {})
        ;
        const isErrorExist = !!Object.values(errorsInTemplates).filter(Boolean).length;
        if (isErrorExist) return [Object.keys(errorsInTemplates), isErrorExist];
        return [Object.keys(errorsInTemplates), false];
    }

    @mobx.action
    private updateEmailConfiguration = async (): Promise<IEmailsTemplateConfiguration> => {
        const tempalateLogo = this.uploadEmailLogoImageS3VM.getImageToApi();
        const response = await this.emailConfigurationDAO.updateTemplatesConfiguration({
            accountId: this.accountId,
            eventId: this.eventId,
            configurations: this.emailTemplates.map(t => ({
                ...t,
                body: transpileClassesToInlineStyles(t.body),
                ...(!this.uploadEmailLogoImageS3VM.getImageChanged ? {} : {...tempalateLogo}),
            })),
        });
        mobx.runInAction(() => {
            const emailTemplates = response.configurations.map(config => {
                const transpiledBody = transpileInlineStylesToClasses(config.body);
                config.body = transpiledBody;
                return config;
            });
            this.emailTemplates = emailTemplates;
            this.initialEmailTemplates = emailTemplates;
            this.uploadEmailLogoImageS3VM.addImage(
                emailTemplates[0].logo?.assetUrl,
                emailTemplates[0].logo?.title,
                emailTemplates[0].logo?.objectKey
            );
            this.uploadEmailLogoImageS3VM.setImageChanged(false);
            this.setEmailTemplatesNames(emailTemplates);
            this.setCurrentTemplate(this.currentTemplate);
            response.configurations = emailTemplates;
        });
        return response;
    };

    @mobx.action
    public handleSubmitEmailForm = async (intl: IntlShape) => {
        this.serverErrorMessage = '';
        const isFormInvalid = this.getTemplateEmailErrors[1];

        if (!this.isEmailFormChanged || isFormInvalid) {
            return false;
        }

        try {
            this.saving = true;
            const response = await this.updateEmailConfiguration();
            showNotification(
                intl.formatMessage({
                    id: 'oe.websiteTab.emailLogoUpdated',
                    description: 'Notification about updated registration email',
                    defaultMessage: 'Registration Email updated'
                })
            );
            this.trackingService.get().track(TRACKING_EVENTS.EMAIL_TEMPLATES_CONFIGURATION, { ...this.toTrackInfo });
            this.setInitialToTrackInfo(response.configurations);
            return true;
        } catch (error) {
            handleApiException(error);
            this.serverErrorMessage = this.onUpdateEmailLogoError(error);
            return false;
        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    @mobx.action
    public handleSendPreview = async (intl: IntlShape, selectedOption: string) => {
        try {
            await this.emailConfigurationDAO.sendEmailPreview(
                this.selectedEmailTemplate.type,
                {
                accountId: this.accountId,
                eventId: this.eventId
            });
            this.trackingService.get().track(TRACKING_EVENTS.SEND_PREVIEW_BUTTON, { 'Sent Email Template': selectedOption });
            showNotification(intl.formatMessage({
                id: 'oe.websiteTab.previewSent',
                defaultMessage: 'Preview sent',
                description: 'Notification message',
            }));

        // eslint-disable-next-line no-empty
        } catch (e) {}
    };

    @mobx.action
    public fetchEmailLogoData = async (): Promise<void> => {
        this.loading = true;
        this.initCurrentTemplate();
        try {
            if (!this.isConfigurePopUpOpen) {
                let response = await this.emailConfigurationDAO.getTemplatesConfiguration({
                    accountId: this.accountId,
                    eventId: this.eventId,
                });
                mobx.runInAction(() => {
                    this.triagedRegistrationEnabled = response.triagedRegistrationEnabled;
                    this.availableSubtypes = response.availableSubtypes ?? [];
                    this.availableTypes = response.availableTypes ?? [];
                });

                const isDefaultEmailTemplatesConfiguration = !response.configurations.length;
                if (isDefaultEmailTemplatesConfiguration) {
                    response = await this.emailConfigurationDAO.getDefaultTemplatesConfiguration({
                        accountId: this.accountId,
                        eventId: this.eventId,
                    });
                }

                mobx.runInAction(() => {
                    const configurations = [...response.configurations].map(value => {
                        const transpiledBody = transpileInlineStylesToClasses(value.body);
                        value.body = transpiledBody;
                        return value;
                    });
                    this.emailTemplates = [...configurations];
                    this.initialEmailTemplates = [...configurations];
                    this.setEmailTemplatesNames(configurations);
                    this.setInitialToTrackInfo(configurations);
                    if (configurations[0].logo) {
                        this.uploadEmailLogoImageS3VM.addImage(
                            configurations[0].logo.assetUrl,
                            configurations[0].logo.title,
                            configurations[0].logo.objectKey
                        );
                    }
                });
            }

        } catch (error) {
            this.serverErrorMessage = this.onUpdateEmailLogoError(error);
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };


    @mobx.action
    public showScheduledEmailForm = (templateTitle: string): void => {
        this.setCurrentTemplate(templateTitle);
        if (this.isConfigurePopUpOpen) {
            this.showConfigureEmailTemplateFormPopupMap.maximize();
        } else {
            this.showConfigureEmailTemplateFormPopupMap = this.subWindowService.showSubWindow({
                withExpand: true,
                title: 'Configure Email',
                className: 'emails-template',
                componentToHost: EmailsTemplateForm,
                onCloseIconClickHandler: () => {
                    this.resetToInitialData();
                },
                componentToHostProps: {
                    vm: this
                },
            });
        }
    };

    @mobx.action
    public setCurrentTemplate = (templateTitle: string): void => {
        this.currentTemplate = templateTitle;
        this.selectedEmailTemplate = this.emailTemplates.find(t => t.name === templateTitle);
    };

    @mobx.action
    public initCurrentTemplate = (): void => {
        if (
            !this.showConfigureEmailTemplateFormPopupMap ||
            this.showConfigureEmailTemplateFormPopupMap.getStatus() === SubWindowStatus.Closed
        ) {
            this.currentTemplate = '';
            this.selectedEmailTemplate = null;
        }
    };

    private get isConfigurePopUpOpen(): boolean {
        return !!this.showConfigureEmailTemplateFormPopupMap
        && this.showConfigureEmailTemplateFormPopupMap.getStatus() !== SubWindowStatus.Closed;
    }

    @mobx.action
    private resetToInitialData = (): void => {
        this.showConfigureEmailTemplateFormPopupMap.close();
        this.emailTemplates = this.initialEmailTemplates;
        this.isEmailFormChanged = false;
        this.uploadEmailLogoImageS3VM.resetImage();
        this.emailTemplatesErrorHandler = {};
        this.setCurrentTemplate('');
        if (this.initialEmailTemplates[0].logo) {
            this.uploadEmailLogoImageS3VM.addImage(
                this.initialEmailTemplates[0].logo.assetUrl,
                this.initialEmailTemplates[0].logo.title,
                this.initialEmailTemplates[0].logo.objectKey
            );
        }
    };

    public get isCreateEmailTemplatePopUpOpen(): boolean {
        return !!this.createEmailTemplateFormPopupMap
        && this.createEmailTemplateFormPopupMap.getStatus() !== SubWindowStatus.Closed;
    }

    @mobx.action
    public showCreateEmailTemplateForm = (): void => {
        if (this.isCreateEmailTemplatePopUpOpen) {
            this.createEmailTemplateFormPopupMap.maximize();
        } else {
            this.createEmailTemplateFormPopupMap = this.subWindowService.showSubWindow({
                withExpand: true,
                isExpandedByDefault: true,
                title: 'New Responsive Email',
                className: 'emails-template',
                componentToHost: CreateEmailTemplatesForm,
                componentToHostProps: {
                    vm: this,
                    intl: this.props.intl,
                },
            });
        }
    };

    public deleteEmailTemplate = () => {
        this.alertsService.confirm({
            message:(
                <>
                    <Text
                        className='alert-message'
                        id='oe.alerts.areYouSure'
                        description='Alert message'
                        message='Are you sure you want to'
                    />
                    <Space />
                    <Text
                        className='alert-message--danger'
                        id='oe.alerts.deleteThisEmail'
                        description='Alert message'
                        message='delete this Email'
                    />?<br /><br />
                    <Text
                        className='alert-message--danger'
                        id='oe.alerts.thisCannotBeUndone.'
                        description='Alert message'
                        message='This cannot be undone.'
                    />
                </>
            ),
            confirmButtonProps: {
                text: 'Yes, Delete',
                modifier: 'confirm-warn',
                handler: this.handleDeleteEmailTemplate,
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action.bound
    public handleDeleteEmailTemplate = async (): Promise<void> => {

        try {
            this.saving = true;
            const emailTemplatesForUpdate = this.initialEmailTemplates.filter(
                t => {
                    if (t.name !== this.selectedEmailTemplate.name) {
                        const body = transpileClassesToInlineStyles(t.body);
                        t.body = body;
                        return t;
                    }
                }
            );
            const response = await this.emailConfigurationDAO.updateTemplatesConfiguration({
                accountId: this.accountId,
                eventId: this.eventId,
                configurations: emailTemplatesForUpdate,
            });

            mobx.runInAction(() => {
                const configurations = [...response.configurations].map(value => {
                    const transpiledBody = transpileInlineStylesToClasses(value.body);
                    value.body = transpiledBody;
                    return value;
                });
                this.emailTemplates = this.emailTemplates.filter(
                    t => t.name !== this.selectedEmailTemplate.name
                );
                this.initialEmailTemplates = this.initialEmailTemplates.filter(
                    t => t.name !== this.selectedEmailTemplate.name
                );
                this.setEmailTemplatesNames(configurations);
                this.setCurrentTemplate('Registration Successful');
            });
        } catch (error) {
            if (error.response?.status === 403) {
                handleApiException(error);
            } else {
                mobx.runInAction(() => {
                    this.serverErrorMessage = this.onUpdateEmailLogoError(error);
                });
            }

        } finally {
            mobx.runInAction(() => {
                this.saving = false;
            });
        }
    };

    public get getEmailConfigurationDAO(): IEmailConfigurationDAO {
        return this.emailConfigurationDAO;
    }

    public get getAccountId(): number {
        return this.accountId;
    }

    public get getEventId(): number {
        return this.eventId;
    }

    public addTemplateForTracking(templateName: string): void {
        if (!Reflect.has(this.toTrackInfo, templateName)) {
            this.toTrackInfo[templateName] = 'No';
        }
    }
}

export { EmailsTemplateFormVM };
